import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT_EMAIL } from "@/lib/config";
import { PaperPlaneTilt, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="border-t" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Get in touch</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3" style={{ color: "var(--bk-text)" }}>
            Have questions? Contact us.
          </h2>
          <p className="text-base mt-4" style={{ color: "var(--bk-text-2)" }}>
            Send us a note and we'll get back to you — or reach us directly anytime.
          </p>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold" style={{ color: "var(--bk-accent)" }}>
            <EnvelopeSimple size={18} weight="duotone" /> {CONTACT_EMAIL}
          </a>
        </div>

        <div className="bk-card p-6">
          {done ? (
            <div className="text-center py-8" data-testid="contact-success">
              <CheckCircle size={48} weight="fill" color="var(--bk-success)" className="mx-auto" />
              <h3 className="font-display text-xl font-bold mt-4" style={{ color: "var(--bk-text)" }}>Thanks — we'll be in touch!</h3>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>We'll reply to your email soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Name</label>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-white text-sm outline-none focus:ring-2 mt-1.5" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Email</label>
                <input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-white text-sm outline-none focus:ring-2 mt-1.5" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} placeholder="you@company.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Message (optional)</label>
                <textarea data-testid="contact-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-white text-sm outline-none focus:ring-2 mt-1.5 resize-y" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} placeholder="Tell us a bit about your bookkeeping / business…" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="bk-btn-primary w-full py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <PaperPlaneTilt size={18} weight="fill" /> {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
